import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { dashboardAPI, eventsAPI, remindersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FolderKanban, CalendarDays, ListTodo, Clock, CheckCircle2, ArrowRight, Send, Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { formatAmount } = useCurrency();
  const [stats, setStats] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('goodMorning');
    if (hour < 18) return t('goodAfternoon');
    return t('goodEvening');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, eventsRes, remindersRes] = await Promise.all([
          dashboardAPI.getStats(),
          eventsAPI.getAll(),
          remindersAPI.getUpcoming()
        ]);
        setStats(statsRes.data);
        const sortedEvents = eventsRes.data
          .filter(e => new Date(e.start) >= new Date())
          .sort((a, b) => new Date(a.start) - new Date(b.start))
          .slice(0, 5);
        setUpcomingEvents(sortedEvents);
        setReminders(remindersRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 w-64 bg-[#E7E5E4] rounded-lg animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-[#E7E5E4] rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: t('pendingOffers'),
      value: stats?.pending_offers || 0,
      icon: Send,
      color: 'text-[#78866B]',
      bgColor: 'bg-[#78866B]/10',
      link: '/intakes'
    },
    {
      title: t('activeProjects'),
      value: stats?.active_projects || 0,
      icon: FolderKanban,
      color: 'text-[#C98E75]',
      bgColor: 'bg-[#C98E75]/10',
      link: '/projects'
    },
    {
      title: t('followUpReminders'),
      value: reminders.length,
      icon: Bell,
      color: 'text-[#C98E75]',
      bgColor: 'bg-[#C98E75]/10',
      link: '/intakes'
    }
  ];

  const stages = [
    { key: 'new_intake', label: t('newIntake'), color: 'bg-[#78866B]' },
    { key: 'consultation', label: t('consultation'), color: 'bg-yellow-500' },
    { key: 'offer_sent', label: t('offerSent'), color: 'bg-blue-500' },
    { key: 'offer_accepted', label: t('offerAccepted'), color: 'bg-[#A8A29E]' }
  ];

  const taskStatuses = [
    { key: 'todo', label: 'To-Do', icon: ListTodo, color: 'text-[#78866B]', bg: 'bg-[#78866B]/10' },
    { key: 'in_progress', label: 'In Progress', icon: Clock, color: 'text-[#C98E75]', bg: 'bg-[#C98E75]/10' },
    { key: 'done', label: 'Done', icon: CheckCircle2, color: 'text-[#57534E]', bg: 'bg-[#57534E]/10' },
  ];

  const quarterLabels = ['Q1', 'Q2', 'Q3', 'Q4'];
  const quarterlyIncome = stats?.quarterly_income || [0, 0, 0, 0];
  const maxQuarterlyIncome = Math.max(...quarterlyIncome, 1);

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="animate-slide-in">
        <h1 className="text-4xl md:text-5xl font-medium text-[#292524] tracking-tight">
          {getGreeting()}, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-[#78716C] mt-2 text-lg">{t('overviewForToday')}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <Link key={stat.title} to={stat.link}>
            <Card 
              className={`border-0 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer animate-slide-in stagger-${index + 1}`}
              data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#78716C] font-medium">{stat.title}</p>
                    <p className="text-3xl font-medium text-[#292524] mt-2">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Two-per-row Widget Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Net Income Per Quarter */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-medium">{t('quarterlyIncome')}</CardTitle>
            <p className="text-sm text-[#78716C]">{t('netIncomeByQuarter')}</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {quarterLabels.map((label, i) => {
                const value = quarterlyIncome[i];
                const pct = maxQuarterlyIncome > 0 ? Math.round((value / maxQuarterlyIncome) * 100) : 0;
                const currentQuarter = Math.floor(new Date().getMonth() / 3);
                const isCurrent = i === currentQuarter;
                return (
                  <div key={label} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className={`font-medium ${isCurrent ? 'text-[#292524]' : 'text-[#78716C]'}`}>
                        {label} {isCurrent && <span className="text-xs text-[#78866B] ml-1">current</span>}
                      </span>
                      <span className={`font-medium ${value > 0 ? 'text-[#78866B]' : 'text-[#A8A29E]'}`}>{formatAmount(value)}</span>
                    </div>
                    <div className="h-2.5 bg-[#F5F5F4] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${isCurrent ? 'bg-[#78866B]' : 'bg-[#78866B]/50'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              <div className="pt-2 border-t border-[#F5F5F4] mt-3">
                <div className="flex justify-between text-sm">
                  <span className="text-[#78716C] font-medium">{t('netIncome')}</span>
                  <span className={`font-medium ${(stats?.net_income || 0) >= 0 ? 'text-[#78866B]' : 'text-red-500'}`}>
                    {formatAmount(stats?.net_income || 0)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Task Overview */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-medium">{t('taskOverview')}</CardTitle>
              <Link to="/tasks" className="text-sm text-[#78866B] hover:underline flex items-center gap-1" data-testid="view-tasks-link">
                {t('viewAll')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {taskStatuses.map(status => {
                const count = stats?.tasks?.[status.key] || 0;
                const StatusIcon = status.icon;
                return (
                  <div key={status.key} className="flex items-center gap-3 p-3 rounded-xl bg-[#F5F5F4]/50">
                    <div className={`w-10 h-10 rounded-xl ${status.bg} flex items-center justify-center`}>
                      <StatusIcon className={`w-5 h-5 ${status.color}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#292524]">{status.label}</p>
                    </div>
                    <span className="text-2xl font-medium text-[#292524]">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Intake Overview */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-medium">{t('intakeOverview')}</CardTitle>
              <Link to="/intakes" className="text-sm text-[#78866B] hover:underline flex items-center gap-1" data-testid="view-intakes-link">
                {t('viewAll')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stages.map((stage) => {
                const count = stats?.intake_stages?.[stage.key] || 0;
                const total = Object.values(stats?.intake_stages || {}).reduce((a, b) => a + b, 0) || 1;
                const percentage = Math.round((count / total) * 100);
                
                return (
                  <div key={stage.key} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#292524] font-medium">{stage.label}</span>
                      <span className="text-[#78716C]">{count}</span>
                    </div>
                    <div className="h-2 bg-[#F5F5F4] rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${stage.color} rounded-full transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-medium">{t('upcomingEvents')}</CardTitle>
              <Link to="/calendar" className="text-sm text-[#78866B] hover:underline flex items-center gap-1" data-testid="view-calendar-link">
                {t('viewCalendar')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {upcomingEvents.length === 0 ? (
              <p className="text-[#78716C] text-center py-8">{t('noUpcomingEvents')}</p>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.map((event) => (
                  <div 
                    key={event.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-[#F5F5F4]/50 hover:bg-[#F5F5F4] transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#78866B]/10 flex flex-col items-center justify-center">
                      <span className="text-xs text-[#78716C]">{format(new Date(event.start), 'MMM')}</span>
                      <span className="text-lg font-medium text-[#292524]">{format(new Date(event.start), 'd')}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#292524] truncate">{event.title}</p>
                      <p className="text-sm text-[#78716C]">{format(new Date(event.start), 'h:mm a')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
